/**
 * Role ladders — the career-path layer over assessments. Departments define
 * tracks of roles, each with expected skill levels; an employee's assessment
 * is compared against their current and next role to surface exactly the
 * skills that matter for advancement (instead of a flat all-skills list).
 *
 * localStorage-first like the other growth stores so ladders will be
 * admin-editable/backend-synced later without component changes.
 */
import { ARCHITECTURE_ROLES } from './architectureRoles';
import { emitGrowthChange } from './events';

const STORAGE_KEY = 'peoplevue_growth_roles';

function defaultLadders() {
  return { [ARCHITECTURE_ROLES.templateId]: ARCHITECTURE_ROLES };
}

function readLocal() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) return stored;
  } catch {
    // fall through
  }
  return defaultLadders();
}

function writeLocal(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

/** The ladder (tracks of roles) for a template, or null. */
export function getLadder(templateId) {
  return readLocal()[templateId] || null;
}

export function saveLadder(templateId, ladder) {
  const map = readLocal();
  map[templateId] = ladder;
  writeLocal(map);
  emitGrowthChange();
  return ladder;
}

/** Ladder for a template, creating an empty one if the department is new. */
export function ensureLadder(templateId) {
  return getLadder(templateId) || { templateId, tracks: [] };
}

/** All roles across tracks, each tagged with its track and position. */
export function getAllRoles(templateId) {
  const ladder = getLadder(templateId);
  if (!ladder) return [];
  return ladder.tracks.flatMap((track) =>
    track.roles.map((role, index) => ({
      ...role,
      trackId: track.id,
      trackName: track.name,
      levelIndex: index,
    }))
  );
}

/**
 * Best-guess role for a job title (exact-ish contains match on
 * titleMatches). Returns null when nothing fits — the UI then asks the
 * person to pick their role themselves.
 */
export function matchRoleForTitle(templateId, jobTitle) {
  const title = (jobTitle || '').toLowerCase().trim();
  if (!title) return null;
  const roles = getAllRoles(templateId);
  // Longest match wins so "senior project architect" beats "project architect".
  let best = null;
  for (const role of roles) {
    for (const candidate of role.titleMatches || []) {
      if (title.includes(candidate) && (!best || candidate.length > best.matchLength)) {
        best = { ...role, matchLength: candidate.length };
      }
    }
  }
  return best;
}

const OVERRIDES_KEY = 'peoplevue_growth_role_overrides';

/**
 * Manual "this is my role" pick — title matching is a best guess and job
 * titles drift, so people (or their manager) can pin the right rung.
 */
export function getRoleOverride(employeeId) {
  try {
    return (JSON.parse(localStorage.getItem(OVERRIDES_KEY)) || {})[employeeId] || null;
  } catch {
    return null;
  }
}

export function setRoleOverride(employeeId, roleId) {
  try {
    const map = JSON.parse(localStorage.getItem(OVERRIDES_KEY)) || {};
    if (roleId) map[employeeId] = roleId;
    else delete map[employeeId];
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
  emitGrowthChange();
}

const ASSIGNMENTS_KEY = 'peoplevue_growth_career_paths';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Captured by syncCareerPaths so later writes can push without every caller
// threading a token (userRolesService pattern).
let apiToken = null;

function readAssignments() {
  try {
    return JSON.parse(localStorage.getItem(ASSIGNMENTS_KEY)) || {};
  } catch {
    return {};
  }
}

function writeAssignments(map) {
  try { localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

/** Explicit (admin- or self-set) career path pick for an employee, or null. */
export function getAssignment(employeeId) {
  if (!employeeId) return null;
  return readAssignments()[employeeId.toLowerCase()] || null;
}

/**
 * Set (or clear, with pick=null) an employee's explicit career path.
 * Local-first: the UI updates immediately; the server push is non-fatal so an
 * unreachable API never blocks the admin. Server enforces admin-or-self.
 */
export function setCareerPathAssignment(employeeId, pick) {
  if (!employeeId) return;
  const key = employeeId.toLowerCase();
  const map = readAssignments();
  if (pick) map[key] = { ...pick, setAt: new Date().toISOString() };
  else delete map[key];
  writeAssignments(map);
  emitGrowthChange();

  if (API_BASE && apiToken) {
    fetch(`${API_BASE}/career-paths/${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
      body: JSON.stringify(pick ?? null),
    })
      .then((res) => { if (!res.ok) console.warn(`Career path push failed: ${res.status}`); })
      .catch((err) => console.warn('Career path push failed:', err.message));
  }
}

/**
 * Hydrate assignments from the server. Call once after sign-in. Non-fatal.
 * Also migrates the old per-device localStorage role override: if this
 * device has a legacy pick the server doesn't know about, push it up once,
 * then drop the legacy key — after that the server copy is the truth.
 */
export async function syncCareerPaths(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/career-paths`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Career paths API ${res.status}`);
    const doc = await res.json();
    const assignments = doc?.assignments && typeof doc.assignments === 'object' ? doc.assignments : {};
    writeAssignments(assignments);

    migrateLegacyOverrides(assignments);
    emitGrowthChange();
    return true;
  } catch (err) {
    console.warn('Failed to sync career paths (non-fatal):', err.message);
    return false;
  }
}

function migrateLegacyOverrides(assignments) {
  let legacy;
  try {
    legacy = JSON.parse(localStorage.getItem(OVERRIDES_KEY)) || {};
  } catch {
    legacy = {};
  }
  const ladders = readLocal();
  Object.entries(legacy).forEach(([employeeId, roleId]) => {
    const key = employeeId.toLowerCase();
    if (assignments[key]) return; // server already has a pick — it wins
    // A legacy override is just a roleId; recover its track/template by
    // searching every ladder (role ids are unique per ladder seed).
    for (const ladder of Object.values(ladders)) {
      for (const track of ladder.tracks) {
        if (track.roles.some((r) => r.id === roleId)) {
          setCareerPathAssignment(key, {
            trackId: track.id, roleId, templateId: ladder.templateId,
          });
          return;
        }
      }
    }
  });
  try { localStorage.removeItem(OVERRIDES_KEY); } catch { /* ignore */ }
}

/**
 * Effective role for an employee. Manual mode (an explicit assignment
 * exists) is authoritative: the assigned role, or null when no role is set
 * or the stored role has been deleted from the ladder — manual people are
 * never re-seeded from their job title. Auto mode falls back through the
 * legacy local override to the title match.
 */
export function getRoleFor(templateId, employee) {
  const assignment = getAssignment(employee?.id);
  if (assignment) {
    if (!assignment.roleId) return null;
    return getAllRoles(templateId).find((r) => r.id === assignment.roleId) || null;
  }
  const overrideId = getRoleOverride(employee?.id);
  if (overrideId) {
    const role = getAllRoles(templateId).find((r) => r.id === overrideId);
    if (role) return role;
  }
  return matchRoleForTitle(templateId, employee?.jobTitle);
}

/** The role after `roleId` on its own track, or null at the top. */
export function nextRoleOnTrack(templateId, roleId) {
  const ladder = getLadder(templateId);
  if (!ladder) return null;
  for (const track of ladder.tracks) {
    const index = track.roles.findIndex((r) => r.id === roleId);
    if (index !== -1) {
      const next = track.roles[index + 1];
      return next ? { ...next, trackId: track.id, trackName: track.name, levelIndex: index + 1 } : null;
    }
  }
  return null;
}
